import React from "react";

/**
 * ValuationBadge — Shared pill rendering valuation verdict.
 *
 * Backend classification (`scoring_engine._classify_valuation`):
 *   discount_pct = (fair_value - current_price) / fair_value × 100
 *
 *   > +30%  → significantly_undervalued  → "Deep Value"   (#14532d, dark green)
 *   > +10%  → undervalued                → "Undervalued"  (#16a34a, medium green)
 *   ±10%    → fairly_valued              → "Fair Value"   (#6b7280, grey)
 *   < -10%  → overvalued                 → "Overvalued"   (#ea580c, orange)
 *   < -30%  → significantly_overvalued   → "Overpriced"   (#dc2626, red)
 *
 * Color palette explicitly approved by stakeholder for clear visual
 * distinction between "Deep Value" and "Undervalued" (previously both
 * looked similar shades of green).
 *
 * @param {object} props
 * @param {string} props.status   - valuation_status enum from API
 * @param {number} [props.discountPct] - signed % (positive = below fair value)
 * @param {"sm"|"md"|"lg"} [props.size="md"]
 * @param {boolean} [props.short=false] - "DEEP" vs "Deep Value"
 * @param {boolean} [props.showIcon=true]
 * @param {boolean} [props.uniform=true] - fixed width (matches SignalBadge)
 * @param {string} [props.className]
 */
const VALUATION_META = {
  significantly_undervalued: {
    short: "DEEP",
    long: "Deep Value",
    icon: "💎",
    // #14532d — dark rich green, signals BEST opportunity
    cls: "bg-[#14532d] text-white border-[#14532d] font-semibold",
  },
  undervalued: {
    short: "UNDER",
    long: "Undervalued",
    icon: "📉",
    // #16a34a — standard medium green, good opportunity
    cls: "bg-[#16a34a] text-white border-[#16a34a] font-medium",
  },
  fairly_valued: {
    short: "FAIR",
    long: "Fair Value",
    icon: "⚖",
    // #6b7280 — neutral grey
    cls: "bg-[#6b7280] text-white border-[#6b7280] font-medium",
  },
  overvalued: {
    short: "OVER",
    long: "Overvalued",
    icon: "📈",
    // #ea580c — orange, caution
    cls: "bg-[#ea580c] text-white border-[#ea580c] font-medium",
  },
  significantly_overvalued: {
    short: "PRICED",
    long: "Overpriced",
    icon: "🔴",
    // #dc2626 — red, avoid
    cls: "bg-[#dc2626] text-white border-[#dc2626] font-semibold",
  },
  no_data: {
    short: "—",
    long: "Insufficient data",
    icon: "—",
    cls: "bg-muted/50 text-muted-foreground border-border italic",
  },
};

const SIZE = {
  sm: "text-[10px] px-2 py-0.5 min-w-[78px]",
  md: "text-xs px-2.5 py-0.5 min-w-[104px]",
  lg: "text-sm px-3 py-1 min-w-[128px]",
};

const ValuationBadge = ({
  status,
  discountPct,
  size = "md",
  short = false,
  showIcon = true,
  uniform = true,
  className = "",
}) => {
  const key = (status || "no_data").toLowerCase();
  const meta = VALUATION_META[key] || VALUATION_META.no_data;
  const label = short ? meta.short : meta.long;
  const sizeCls = uniform
    ? SIZE[size] || SIZE.md
    : (size === "sm"
      ? "text-[10px] px-1.5 py-0.5"
      : size === "lg"
        ? "text-sm px-2.5 py-1"
        : "text-xs px-2 py-0.5");
  const tooltip =
    key === "no_data"
      ? "Not enough data to compute fair value (no PE / PB / DDM / Graham)."
      : discountPct != null
        ? `${meta.long} · ${discountPct > 0 ? "+" : ""}${discountPct.toFixed(1)}% vs fair value`
        : meta.long;
  const showIconActual = showIcon && key !== "no_data";
  return (
    <span
      data-testid={`valuation-badge-${key}`}
      title={tooltip}
      className={`inline-flex items-center justify-center gap-1 rounded-full border whitespace-nowrap ${sizeCls} ${meta.cls} ${className}`}
    >
      {showIconActual && (
        <span aria-hidden="true" className="leading-none text-[0.95em]">
          {meta.icon}
        </span>
      )}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// VALUATION_STATUS_OPTIONS — exported helper for filter chips / dropdowns.
// ---------------------------------------------------------------------------
export const VALUATION_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "undervalued", label: "Undervalued" },        // matches both deep value and undervalued
  { value: "fairly_valued", label: "Fair value" },
  { value: "overvalued", label: "Overvalued" },          // matches both overvalued and overpriced
];

// Helper: is the row in the requested filter bucket?
export const matchesValuationFilter = (status, filterValue) => {
  if (!filterValue || filterValue === "all") return true;
  const s = (status || "").toLowerCase();
  if (filterValue === "undervalued") {
    return s === "undervalued" || s === "significantly_undervalued";
  }
  if (filterValue === "overvalued") {
    return s === "overvalued" || s === "significantly_overvalued";
  }
  if (filterValue === "fairly_valued") {
    return s === "fairly_valued";
  }
  return true;
};

// Helper: numeric rank for sorting (most undervalued = highest rank, sorts first DESC)
export const valuationSortRank = (status) => {
  const s = (status || "").toLowerCase();
  return {
    significantly_undervalued: 5,
    undervalued: 4,
    fairly_valued: 3,
    overvalued: 2,
    significantly_overvalued: 1,
    no_data: 0,
  }[s] || 0;
};

export default ValuationBadge;
